// src/components/common/Pagination.js
import React from 'react';

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems = 0,
    itemsPerPage = 20,
    showStats = true,
    size = 'medium' // small, medium, large
}) => {
    if (totalPages <= 1) return null;

    const getVisiblePages = () => {
        const delta = size === 'small' ? 1 : 2;
        const range = [];
        const rangeWithDots = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i);
        }

        if (currentPage - delta > 2) {
            rangeWithDots.push(1, '...');
        } else {
            rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push('...', totalPages);
        } else if (totalPages > 1) {
            rangeWithDots.push(totalPages);
        }

        return rangeWithDots;
    };

    const visiblePages = getVisiblePages();

    const handlePageClick = (page) => {
        if (page === '...' || page === currentPage) return;
        onPageChange(page);
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    // Calculate stats
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <div className={`pagination-container ${size}`}>
            {showStats && totalItems > 0 && (
                <div className="pagination-stats">
                    <span>
                        Showing {startItem.toLocaleString()} to {endItem.toLocaleString()} of {totalItems.toLocaleString()} entries
                    </span>
                </div>
            )}

            <nav className="pagination" aria-label="Pagination Navigation">
                {/* Previous Button */}
                <button
                    className={`pagination-btn pagination-prev ${currentPage === 1 ? 'disabled' : ''}`}
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                >
                    <i className="fas fa-chevron-left"></i>
                    <span className="pagination-text">Previous</span>
                </button>

                {/* Page Numbers */}
                <div className="pagination-pages">
                    {visiblePages.map((page, index) => (
                        <button
                            key={index}
                            className={`pagination-page ${page === currentPage ? 'active' : ''
                                } ${page === '...' ? 'dots' : ''}`}
                            onClick={() => handlePageClick(page)}
                            disabled={page === '...'}
                            aria-label={page === '...' ? 'More pages' : `Go to page ${page}`}
                            aria-current={page === currentPage ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}
                </div>

                {/* Next Button */}
                <button
                    className={`pagination-btn pagination-next ${currentPage === totalPages ? 'disabled' : ''}`}
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                >
                    <span className="pagination-text">Next</span>
                    <i className="fas fa-chevron-right"></i>
                </button>
            </nav>

            <style jsx>{`
        .pagination-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0 0 12px 12px;
          gap: 1rem;
        }

        .pagination-container.small {
          padding: 0.75rem;
          font-size: 0.875rem;
        }

        .pagination-container.large {
          padding: 1.5rem;
          font-size: 1.125rem;
        }

        .pagination-stats {
          color: #6b7280;
          font-size: 0.875rem;
          white-space: nowrap;
        }

        .pagination-container.small .pagination-stats {
          font-size: 0.8rem;
        }

        .pagination-container.large .pagination-stats {
          font-size: 1rem;
        }

        .pagination {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pagination-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
        }

        .pagination-container.small .pagination-btn {
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
        }

        .pagination-container.large .pagination-btn {
          padding: 0.75rem 1.25rem;
          font-size: 1rem;
        }

        .pagination-btn:hover:not(.disabled) {
          border-color: #3b82f6;
          color: #3b82f6;
          background: #eff6ff;
        }

        .pagination-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f9fafb;
        }

        .pagination-pages {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .pagination-page {
          min-width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .pagination-container.small .pagination-page {
          min-width: 32px;
          height: 32px;
          font-size: 0.8rem;
        }

        .pagination-container.large .pagination-page {
          min-width: 48px;
          height: 48px;
          font-size: 1rem;
        }

        .pagination-page:hover:not(.active):not(.dots) {
          border-color: #3b82f6;
          color: #3b82f6;
          background: #eff6ff;
        }

        .pagination-page.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        .pagination-page.dots {
          border: none;
          background: transparent;
          cursor: default;
          color: #6b7280;
        }

        .pagination-page.dots:hover {
          border: none;
          background: transparent;
          color: #6b7280;
        }

        .pagination-text {
          display: inline;
        }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .pagination-container {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }

          .pagination {
            justify-content: center;
          }

          .pagination-stats {
            text-align: center;
            order: -1;
          }

          .pagination-text {
            display: none;
          }

          .pagination-btn {
            min-width: 40px;
            justify-content: center;
          }

          .pagination-pages {
            gap: 0.125rem;
          }

          .pagination-page {
            min-width: 32px;
            height: 32px;
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .pagination-container {
            padding: 0.75rem 0.5rem;
          }

          .pagination-pages {
            flex-wrap: wrap;
            justify-content: center;
          }

          .pagination-page {
            min-width: 28px;
            height: 28px;
            font-size: 0.75rem;
          }

          .pagination-btn {
            padding: 0.375rem 0.5rem;
            min-width: 36px;
            height: 36px;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .pagination-btn, .pagination-page {
            border-width: 2px;
          }
          
          .pagination-page.active {
            border-color: #1d4ed8;
            background: #1d4ed8;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .pagination-btn, .pagination-page {
            transition: none;
          }
        }

        /* Focus styles for accessibility */
        .pagination-btn:focus,
        .pagination-page:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Dark mode support (if you have dark mode) */
        @media (prefers-color-scheme: dark) {
          .pagination-container {
            background: #1f2937;
            border-color: #374151;
          }

          .pagination-stats {
            color: #d1d5db;
          }

          .pagination-btn,
          .pagination-page {
            background: #374151;
            border-color: #4b5563;
            color: #d1d5db;
          }

          .pagination-btn:hover:not(.disabled),
          .pagination-page:hover:not(.active):not(.dots) {
            background: #4b5563;
            border-color: #60a5fa;
            color: #60a5fa;
          }

          .pagination-page.active {
            background: #3b82f6;
            border-color: #3b82f6;
          }

          .pagination-btn.disabled {
            background: #1f2937;
            color: #6b7280;
          }
        }
      `}</style>
        </div>
    );
};

export default Pagination;